import type { PendingUsageSidebar, SequenceSettingsDraft, UsageSidebarOrigin } from './usage-sidebar-store';

/**
 * Plan 2026-09-29-001 U2 (rulebook R14; Codex round-4 B5) — what a settings
 * control (sender, CC/reply-to, flow id, rename) should OPEN on.
 *
 * The bug: a stale refusal parks the rep's typed value on the Save panel's
 * pending. The control itself used to close, and reopening it re-seeded from
 * the SAVED value — so the rep saw the old sender, not the one they had just
 * typed, even though their value was still sitting on the pending. U2's
 * first enforcement point is that the control no longer closes on a refusal;
 * this is the second: if the rep does close it (clicking the Save panel's
 * "Reload to see their change" is an outside click, which closes a Glaze
 * popover), reopening restores the pending's value.
 *
 * Matched on the GROUP (`origin.rungId`) and the FIELD — never the piece.
 * Reload can retarget the pending onto whatever the group now points at
 * while the rep's typing rides along unchanged (R14), so comparing target
 * ids would lose the restore exactly when it matters most.
 *
 * `null` (restore nothing, seed from the saved value) whenever the pending
 * is not this control's own: a different field, a different group, a group
 * with no server id yet (two staged groups both read `rungId: null` and must
 * never share), a Library/playbook doorway on either side (no play group to
 * match), or a delete pending (nothing was typed).
 *
 * Pure: the caller passes the pending in (`useUsageSidebarStore.getState().pending`),
 * so this module imports types only and is directly testable under `bun test`.
 */
export function settingsDraftForReopen(
  pending: PendingUsageSidebar | null,
  usageOrigin: UsageSidebarOrigin | undefined,
  field: SequenceSettingsDraft['field'],
): SequenceSettingsDraft | null {
  if (pending === null || pending.mode !== 'save') return null;
  if (usageOrigin === undefined || usageOrigin.kind !== 'play') return null;
  if (pending.origin.kind !== 'play') return null;
  if (pending.origin.rungId === null || pending.origin.rungId !== usageOrigin.rungId) return null;
  const draft = pending.draftSettings;
  if (draft === undefined || draft.field !== field) return null;
  return draft;
}
